/**
 * RatingDistribution — 1..5 star histogram with optional prev-period overlay
 * (IA §4.1.3 / Plan 07 Task 7.3).
 *
 * Input contract: `buckets` is a 5-element `RatingBucket[]` (one per star).
 * When `previous` is supplied, a second muted bar is rendered per bucket,
 * grouped alongside the current bar. Recharts picks up the two `<Bar>`
 * elements as two series; `barGap={2}` gives them a visible gutter so the
 * comparison reads cleanly.
 *
 * States:
 *   - loading  → fixed-height skeleton (matches TrendChart convention).
 *   - empty    → "No ratings in selected period" centred message.
 *   - normal   → BarChart with 5 buckets.
 *
 * Note: app's Overview tab uses `<Donut>` instead of this component — the
 * Quality tab is the first consumer here. The shape is intentionally small:
 * this renders a histogram, not a title or surrounding card. Callers wrap
 * in a `<Card>` for framing.
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ChartTooltip,
  useChartTooltipTrigger,
  useIsTouchOnly,
} from '@/components/charts/ChartTooltip';
import { chartPalette, ratingColor } from '@/lib/chart-palette';
import type { RatingBucket } from '@/services/api-types';

export interface RatingDistributionProps {
  /** 5-element histogram for the current window (rating 1..5). */
  buckets: RatingBucket[];
  /** Optional 5-element histogram for the previous comparable window. */
  previous?: RatingBucket[];
  loading?: boolean;
  /** Pixel height of the responsive container. Default 240. */
  height?: number;
  /**
   * Click handler wired to each bar. When provided the `Current` series
   * becomes interactive (cursor + hover); the `Previous` series is never
   * clickable because the drill-down only has a current-period filter.
   */
  onBarClick?: (rating: 1 | 2 | 3 | 4 | 5) => void;
}

interface MergedBucket {
  rating: string;
  count: number;
  prev?: number;
}

/**
 * Merge current + optional previous into a single row per rating bucket.
 * Missing buckets fall back to 0 so the bar-group stays aligned even on
 * sparse data (backend already zero-pads, but defend against drift).
 */
function mergeBuckets(
  current: RatingBucket[],
  previous: RatingBucket[] | undefined,
): MergedBucket[] {
  const prevByRating = new Map<number, number>();
  if (previous) {
    for (const b of previous) prevByRating.set(b.rating, b.count);
  }
  return current.map((b) => ({
    rating: `${b.rating}★`,
    count: b.count,
    ...(previous ? { prev: prevByRating.get(b.rating) ?? 0 } : {}),
  }));
}

export function RatingDistribution({
  buckets,
  previous,
  loading = false,
  height = 240,
  onBarClick,
}: RatingDistributionProps): React.ReactElement {
  // Mobile tap persistence — see ChartTooltip.useChartTooltipTrigger.
  const tooltipTrigger = useChartTooltipTrigger();
  // On touch-only devices `activeBar` never fires (no hover), so users have
  // no cue that a bar is clickable. Paint a subtle foreground stroke on
  // every clickable bar permanently when hover is unavailable.
  const isTouchOnly = useIsTouchOnly();
  if (loading) {
    return (
      <div
        data-testid="rating-distribution-skeleton"
        className="w-full rounded bg-muted animate-pulse"
        style={{ height }}
      />
    );
  }

  const hasData = buckets && buckets.length > 0 && buckets.some((b) => b.count > 0);
  if (!hasData) {
    return (
      <div
        data-testid="rating-distribution-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No ratings in selected period
      </div>
    );
  }

  const merged = mergeBuckets(buckets, previous);
  const total = buckets.reduce((acc, b) => acc + (b.count ?? 0), 0);
  // Compare mode is driven entirely by the presence of `previous` — the
  // caller (QualityPage) passes `previous={undefined}` when the "vs prev
  // period" checkbox is off. A lone "Current" legend entry adds noise when
  // there's no second series to disambiguate, so we hide the Legend entirely
  // in that case (and keep it when compare is active so users can tell the
  // two bar colours apart).
  const hasCompare = Boolean(previous);

  return (
    <div
      data-testid="rating-distribution"
      data-has-previous={previous ? 'true' : 'false'}
      className="relative"
      style={{ width: '100%', height }}
    >
      <div
        data-testid="rating-distribution-total"
        className="pointer-events-none absolute right-2 top-1 z-10 text-[11px] uppercase tracking-wide text-muted-foreground tabular-nums"
      >
        Total:{' '}
        <span className="font-semibold text-foreground">
          {total.toLocaleString('en-US')}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={merged}
          // top=24 reserves a band above the plot area for the absolute-
          // positioned total badge so it never collides with a tall bar.
          margin={{ top: 24, right: 30, left: 0, bottom: 0 }}
          barGap={2}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="rating" />
          <YAxis allowDecimals={false} />
          {/*
            bug F1 — themed tooltip keeps visual parity across all charts.
            v1.4 dashboard-ux-fix (2026-04-24): on touch-only devices, a
            clickable bar opens TraceDrillDownModal on tap. The modal's
            Radix backdrop covers the tooltip instantly — the user reads
            it as "tooltip flashes for half a second then disappears".
            Suppress the tooltip entirely when touch + drill-down: the
            modal IS the detail view in that mode.
          */}
          {!(isTouchOnly && onBarClick) && (
            <Tooltip
              {...tooltipTrigger}
              cursor={false}
              content={<ChartTooltip />}
            />
          )}
          {hasCompare && <Legend />}
          {/* 2026-04-24 (revised): only the 5★ bucket is pinned to the
              Quality-tab RM-badge green (`chartPalette.rmBadge` = #16a34a).
              Buckets 1-4 stay on the diverging `ratingColor` ramp
              (red → orange → amber → lime) so the "best rating" still
              visually pops while the rest of the histogram keeps its
              semantic signal. Bar-level `fill` is only used for the legend
              swatch; per-bucket Cells below override it on the bars. */}
          <Bar
            dataKey="count"
            name="Current"
            fill={chartPalette.rmBadge}
            isAnimationActive={false}
            cursor={onBarClick ? 'pointer' : 'default'}
            // v1.4 dashboard-ux-fix: when drill-down is wired, paint a
            // foreground-stroke outline on the hovered bar so users can
            // see the bar is interactive at a glance. Mirrors the Donut
            // selected-slice affordance so the click-to-drill vocabulary
            // reads the same across charts.
            activeBar={
              onBarClick
                ? { stroke: 'hsl(var(--foreground))', strokeWidth: 1.5 }
                : false
            }
            // Touch-only fallback: hover never fires on phones/tablets, so
            // we paint the outline permanently — same visual vocabulary,
            // lighter weight so it reads as affordance rather than selection.
            stroke={
              onBarClick && isTouchOnly ? 'hsl(var(--foreground))' : undefined
            }
            strokeWidth={onBarClick && isTouchOnly ? 1 : 0}
            strokeOpacity={onBarClick && isTouchOnly ? 0.6 : 1}
            onClick={
              onBarClick
                ? (payload: unknown) => {
                    // Recharts passes the row payload as the handler arg —
                    // our merged row is `{ rating: '1★', count, prev? }`.
                    const label = (payload as { rating?: string })?.rating;
                    const match = label?.match(/^([1-5])★$/);
                    if (match) {
                      onBarClick(Number(match[1]) as 1 | 2 | 3 | 4 | 5);
                    }
                  }
                : undefined
            }
          >
            {merged.map((row) => {
              const match = row.rating.match(/^([1-5])★$/);
              const n = match
                ? (Number(match[1]) as 1 | 2 | 3 | 4 | 5)
                : null;
              const fill =
                n === 5 ? chartPalette.rmBadge : ratingColor(n);
              return (
                <Cell
                  key={row.rating}
                  fill={fill}
                  data-testid={`rating-distribution-cell-${match?.[1] ?? 'x'}`}
                />
              );
            })}
          </Bar>
          {previous && (
            <Bar
              dataKey="prev"
              fill={chartPalette.mutedSoft}
              name="Previous"
              isAnimationActive={false}
              data-testid="rating-distribution-prev-series"
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
